import { useEffect, useState } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ScenarioRunEvent, ScenarioRunScenario } from '../../../api/api';
import { runDuration, runTime } from '../../../utils/scenarioRunFormat';
import { Outcome, outcomeOfScenario } from '../ScenarioRunTable';
import { CheckTable } from './CheckTable';
import { scenarioCallsLabel, scenarioCallsTitle } from './exactCallsModel';
import { PhaseBar } from './PhaseBar';
import { ScenarioFlow } from './ScenarioFlow';
import { FOLLOW_ATTRIBUTE } from './useFollowRun';
import {
    checkKind,
    errorsBesideChecks,
    scenarioPhases,
    warningsBesideChecks,
} from './runDetailModel';

const LOOK: Record<Outcome, { icon: IconProp; label: string }> = {
    passed: { icon: 'circle-check', label: 'passed' },
    warnings: { icon: 'triangle-exclamation', label: 'passed with warnings' },
    failed: { icon: 'circle-xmark', label: 'failed' },
    running: { icon: 'spinner', label: 'running' },
    skipped: { icon: 'minus', label: 'skipped' },
    pending: { icon: 'clock', label: 'to go' },
};

// As long as the fold in RunFollow.scss takes: the body stays until it has folded shut.
const FOLD_MS = 300;

// A scenario tagged known-failure is meant to fail until a decision is made; red there is news only when it turns green.
const KNOWN_FAILURE = 'known-failure';

export const LogLines = ({ events }: { events: ScenarioRunEvent[] }) => (
    <ol className="rd-log">
        {events.map((event, index) => (
            <li className={`rd-log__line rd-log__line--${event.level ?? 'info'}`} key={index}>
                <span className="rd-log__time">{runTime(event.atUtc)}</span>
                <span>{event.message}</span>
            </li>
        ))}
    </ol>
);

/**
 * Folds open and shut over a moment rather than at once: the body is drawn before it opens and kept until it has
 * shut, and only clips its content while it moves.
 */
const useFold = (open: boolean) => {
    const [drawn, setDrawn] = useState(open);
    const [unfolded, setUnfolded] = useState(open);
    const [settled, setSettled] = useState(open);

    useEffect(() => {
        setSettled(false);

        if (open) {
            setDrawn(true);

            // One frame drawn shut first, so there is something to unfold from.
            const frame = requestAnimationFrame(() => requestAnimationFrame(() => setUnfolded(true)));
            const timer = setTimeout(() => setSettled(true), FOLD_MS);

            return () => {
                cancelAnimationFrame(frame);
                clearTimeout(timer);
            };
        }

        setUnfolded(false);

        const timer = setTimeout(() => setDrawn(false), FOLD_MS);

        return () => clearTimeout(timer);
    }, [open]);

    return { drawn, unfolded, settled };
};

/**
 * One scenario of the run: what it tests and how it went in the header; what went wrong, where the time went, the
 * checks and how to find its documents in the body.
 *
 * Open by itself when the scenario failed, warned or is still going; a pass is one line until somebody asks. Shuts
 * by itself when the page that follows the run moves on from it ({@link collapsed}).
 */
export const ScenarioCard = ({
    scenario,
    events,
    collapsed = false,
}: {
    scenario: ScenarioRunScenario;
    events: ScenarioRunEvent[];
    collapsed?: boolean;
}) => {
    const outcome = outcomeOfScenario(scenario).outcome as Outcome;
    const opensByItself = outcome === 'failed' || outcome === 'warnings' || outcome === 'running';
    const [open, setOpen] = useState(opensByItself);

    // A scenario that turns red while the page is open should open, not wait for a click.
    useEffect(() => {
        if (opensByItself) setOpen(true);
    }, [opensByItself]);

    useEffect(() => {
        if (collapsed) setOpen(false);
    }, [collapsed]);

    const fold = useFold(open);

    const checks = scenario.checks ?? [];
    const failedChecks = checks.filter((check) => checkKind(check.status) === 'failed');
    const passedChecks = checks.filter((check) => checkKind(check.status) === 'passed').length;
    const errors = errorsBesideChecks(scenario);
    const warnings = warningsBesideChecks(scenario);
    const evidence = Object.entries(scenario.evidence ?? {});
    const { phases, fromLog } = scenarioPhases(scenario, events);
    const knownFailure = (scenario.tags ?? []).includes(KNOWN_FAILURE);
    const look = LOOK[outcome];
    const bodyId = `scenario-${scenario.id}-body`;

    // Closed, the header says what failed first, so a list of red rows can be read without opening them.
    const firstProblem = failedChecks[0]
        ? `${failedChecks[0].name}: expected ${failedChecks[0].expected}, got ${failedChecks[0].actual}`
        : errors[0] ?? scenario.skipReason;

    return (
        <li
            {...{ [FOLLOW_ATTRIBUTE]: scenario.id }}
            className={`rd-scn rd-scn--${outcome}${open ? ' rd-scn--open' : ''}${
                knownFailure && outcome === 'failed' ? ' rd-scn--expected' : ''
            }`}
        >
            <button
                type="button"
                className="rd-scn__head"
                aria-expanded={open}
                aria-controls={bodyId}
                onClick={() => setOpen(!open)}
            >
                <FontAwesomeIcon
                    icon={look.icon}
                    spin={outcome === 'running'}
                    className="rd-scn__icon"
                    title={look.label}
                />
                <span className="rd-scn__text">
                    <span className="rd-scn__title">{scenario.description || scenario.id}</span>
                    {open && scenario.why && <span className="rd-scn__why">{scenario.why}</span>}
                    {!open && firstProblem && (
                        <span className="rd-scn__problem">
                            {firstProblem}
                            {failedChecks.length > 1 && ` (+${failedChecks.length - 1} more)`}
                        </span>
                    )}
                    {outcome === 'running' && scenario.step && (
                        <span className="rd-scn__step">{scenario.step}</span>
                    )}
                </span>
                <span className="rd-scn__meta">
                    {knownFailure && outcome === 'failed' && (
                        <span
                            className="rd-tag rd-tag--expected"
                            title="Tagged known-failure in the suite: this fails until the open decision is taken."
                        >
                            expected to fail
                        </span>
                    )}
                    {knownFailure && outcome === 'passed' && (
                        <span
                            className="rd-tag rd-tag--fixed"
                            title="Tagged known-failure in the suite, but it passes: drop the tag."
                        >
                            passes now
                        </span>
                    )}
                    <code className="rd-scn__id">{scenario.id}</code>
                    {checks.length > 0 && (
                        <span className="rd-scn__count" title="Checks that passed">
                            {passedChecks}/{checks.length}
                        </span>
                    )}
                    {scenarioCallsLabel(scenario.connectorExactCalls, scenario.runnerExactCalls) && (
                        <span
                            className="rd-scn__count"
                            title={scenarioCallsTitle(scenario.connectorExactCalls, scenario.runnerExactCalls)}
                        >
                            {scenarioCallsLabel(scenario.connectorExactCalls, scenario.runnerExactCalls)}
                        </span>
                    )}
                    <span className="rd-scn__duration">
                        {runDuration(scenario.startedAtUtc, scenario.finishedAtUtc)}
                    </span>
                    <FontAwesomeIcon icon={open ? 'angle-up' : 'angle-down'} className="rd-scn__chevron" />
                </span>
            </button>

            {fold.drawn && (
                <div
                    className={`rd-scn__fold${fold.unfolded ? ' rd-scn__fold--open' : ''}${
                        fold.settled ? ' rd-scn__fold--settled' : ''
                    }`}
                >
                    <div>
                        <div className="rd-scn__body" id={bodyId}>
                            {(scenario.skipReason || errors.length > 0 || warnings.length > 0) && (
                                <ul className="rd-notes">
                                    {scenario.skipReason && (
                                        <li className="rd-notes__item rd-notes__item--skipped">
                                            <FontAwesomeIcon icon="ban" /> Not run: {scenario.skipReason}
                                        </li>
                                    )}
                                    {errors.map((error, index) => (
                                        <li className="rd-notes__item rd-notes__item--error" key={`e${index}`}>
                                            <FontAwesomeIcon icon="circle-xmark" /> {error}
                                        </li>
                                    ))}
                                    {warnings.map((warning, index) => (
                                        <li className="rd-notes__item rd-notes__item--warning" key={`w${index}`}>
                                            <FontAwesomeIcon icon="triangle-exclamation" /> {warning}
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <ScenarioFlow scenario={scenario} events={events} running={outcome === 'running'} />

                            {phases.length > 0 && (
                                <div className="rd-part">
                                    <h4 className="rd-part__title">Where the time went</h4>
                                    <PhaseBar
                                        phases={phases}
                                        note={fromLog ? 'Read from the log: this run is from before the runner recorded its phases.' : undefined}
                                    />
                                </div>
                            )}

                            {checks.length > 0 && (
                                <div className="rd-part">
                                    <h4 className="rd-part__title">Checks</h4>
                                    <CheckTable scenario={scenario} />
                                </div>
                            )}

                            {evidence.length > 0 && (
                                <div className="rd-part">
                                    <h4 className="rd-part__title">Find it in Chargebee and Exact</h4>
                                    <dl className="rd-evidence">
                                        {evidence.map(([key, value]) => (
                                            <div className="rd-evidence__item" key={key}>
                                                <dt>{key}</dt>
                                                <dd>{value}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                            )}

                            {events.length > 0 && (
                                <details className="rd-part rd-part--log">
                                    <summary className="rd-part__title">Log ({events.length} lines)</summary>
                                    <LogLines events={events} />
                                </details>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </li>
    );
};
